import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, X, LogOut, User } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { logOut, hasRole } from '../services/auth';
import HostApplicationModal from '../features/host/components/HostApplicationModal';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('');
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [showHostApplicationModal, setShowHostApplicationModal] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  const userMenuRef = useRef(null);

  const handleLogout = async () => {
    try {
      await logOut();
      setIsUserMenuOpen(false);
      setIsMenuOpen(false);
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const isHost = hasRole(user, 'host');
  const isClient = hasRole(user, 'client');
  const showHostApplication = user && !isHost && !user.approvedAsHost;

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setIsUserMenuOpen(false);
      }
    };

    if (isUserMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isUserMenuOpen]);

  // Smooth scroll function
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const headerHeight = 64; // Height of sticky header
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerHeight;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
    setIsMenuOpen(false); // Close mobile menu after clicking
  };

  // Handle scroll to update active section
  useEffect(() => {
    const handleScroll = () => {
      const sections = ['home', 'features', 'cta'];
      const scrollPosition = window.scrollY + 100;

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Check on mount
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { id: 'features', label: 'Features' },
    { id: 'cta', label: 'Get Started' }
  ];

  return (
    <header className="border-b border-emerald-100 sticky top-0 bg-white/80 backdrop-blur-md z-40">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between h-16 px-4 sm:px-6 md:px-8">
          {/* Logo */}
          <div className="flex-shrink-0">
            <Link 
              to="/"
              onClick={() => {
                window.scrollTo({ top: 0, behavior: 'smooth' });
                setIsMenuOpen(false);
              }}
              className="flex items-center space-x-2 sm:space-x-3"
            >
              <img
                src={`${process.env.PUBLIC_URL || ''}/logo192.png`}
                alt="Spirara"
                className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl sm:rounded-2xl shadow-lg"
              />
              <span className="text-xl sm:text-2xl font-light text-gray-900">
                Spirara<span className="text-emerald-700">.</span>
                <span className="text-xs text-emerald-600 ml-2 font-normal">Host</span>
              </span>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-1 lg:space-x-2">
            {navItems.map((item) => (
              <a
                key={item.id}
                href={`#${item.id}`}
                onClick={(e) => {
                  e.preventDefault();
                  scrollToSection(item.id);
                }}
                className={`px-4 py-2 rounded-lg text-sm lg:text-base font-medium transition-colors duration-200 ${
                  activeSection === item.id
                    ? 'text-emerald-700 bg-emerald-50'
                    : 'text-gray-700 hover:text-emerald-700 hover:bg-emerald-50/50'
                }`}
              >
                {item.label}
              </a>
            ))}
          </nav>

          {/* Desktop CTA */}
          <div className="hidden md:flex items-center gap-3 ml-4 lg:ml-6">
            {user ? (
              <div className="relative flex items-center gap-3">
                {isHost ? (
                  <button
                    onClick={() => navigate('/host/dashboard')}
                    className="bg-emerald-700 hover:bg-emerald-800 text-white text-sm lg:text-base px-5 lg:px-6 py-2.5 lg:py-3 rounded-lg transition duration-300 shadow-lg hover:shadow-xl font-medium"
                  >
                    Dashboard
                  </button>
                ) : showHostApplication ? (
                  <button
                    onClick={() => setShowHostApplicationModal(true)}
                    className="bg-emerald-700 hover:bg-emerald-800 text-white text-sm lg:text-base px-5 lg:px-6 py-2.5 lg:py-3 rounded-lg transition duration-300 shadow-lg hover:shadow-xl font-medium"
                  >
                    Apply to Host
                  </button>
                ) : null}
                
                {/* User Menu */}
                <div className="relative" ref={userMenuRef}>
                  <button
                    onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-50/50 hover:bg-emerald-50 transition-colors"
                  >
                    {user?.photoURL ? (
                      <img 
                        src={user.photoURL} 
                        alt={user.displayName || 'User'} 
                        className="w-8 h-8 rounded-full"
                      />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-emerald-600 flex items-center justify-center text-white text-sm font-medium">
                        {(user?.displayName || user?.email || 'U')[0].toUpperCase()}
                      </div>
                    )}
                  </button>

                  {/* Dropdown Menu */}
                  {isUserMenuOpen && (
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                      <div className="px-4 py-2 border-b border-gray-200">
                        <p className="text-sm font-medium text-gray-900">
                          {user?.displayName || 'User'}
                        </p>
                        <p className="text-xs text-gray-500 truncate">
                          {user?.email}
                        </p>
                      </div>
                      {showHostApplication && (
                        <button
                          onClick={() => {
                            setIsUserMenuOpen(false);
                            setShowHostApplicationModal(true);
                          }}
                          className="w-full flex items-center gap-3 px-4 py-2 text-sm text-emerald-700 hover:bg-emerald-50 transition-colors text-left"
                        >
                          <User className="w-4 h-4" />
                          Apply to Host
                        </button>
                      )}
                      <button
                        onClick={handleLogout}
                        className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-red-50 hover:text-red-700 transition-colors"
                      >
                        <LogOut className="w-4 h-4" />
                        Sign Out
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <>
                <button
                  onClick={() => navigate('/login')}
                  className="text-gray-700 hover:text-emerald-700 text-sm lg:text-base px-4 lg:px-5 py-2.5 lg:py-3 rounded-lg transition duration-300 font-medium"
                >
                  Sign In
                </button>
                <button
                  onClick={() => navigate('/signup')}
                  className="bg-emerald-700 hover:bg-emerald-800 text-white text-sm lg:text-base px-5 lg:px-6 py-2.5 lg:py-3 rounded-lg transition duration-300 shadow-lg hover:shadow-xl font-medium"
                >
                  Become a Host
                </button>
              </>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="p-2 rounded-lg text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors"
              aria-label="Toggle menu"
            >
              {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {isMenuOpen && (
          <div className="md:hidden border-t border-emerald-100 bg-white">
            <nav className="px-4 py-3 space-y-1">
              {navItems.map((item) => (
                <a
                  key={item.id}
                  href={`#${item.id}`}
                  onClick={(e) => {
                    e.preventDefault();
                    scrollToSection(item.id);
                  }}
                  className={`block px-4 py-3 rounded-lg text-base font-medium transition-colors duration-200 ${
                    activeSection === item.id
                      ? 'text-emerald-700 bg-emerald-50'
                      : 'text-gray-700 hover:text-emerald-700 hover:bg-emerald-50/50'
                  }`}
                >
                  {item.label}
                </a>
              ))}
              {user ? (
                <>
                  {isHost ? (
                    <button
                      onClick={() => {
                        setIsMenuOpen(false);
                        navigate('/host/dashboard');
                      }}
                      className="block w-full mt-2 bg-emerald-700 hover:bg-emerald-800 text-white text-base px-6 py-3 rounded-lg transition duration-300 shadow-lg font-medium text-center"
                    >
                      Dashboard
                    </button>
                  ) : showHostApplication ? (
                    <button
                      onClick={() => {
                        setIsMenuOpen(false);
                        setShowHostApplicationModal(true);
                      }}
                      className="block w-full mt-2 bg-emerald-700 hover:bg-emerald-800 text-white text-base px-6 py-3 rounded-lg transition duration-300 shadow-lg font-medium text-center"
                    >
                      Apply to Host
                    </button>
                  ) : null}
                  
                  <div className="border-t border-emerald-100 mt-3 pt-3">
                    <div className="flex items-center gap-3 px-4 py-2 mb-2">
                      {user?.photoURL ? (
                        <img 
                          src={user.photoURL} 
                          alt={user.displayName || 'User'} 
                          className="w-10 h-10 rounded-full"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-emerald-600 flex items-center justify-center text-white font-medium">
                          {(user?.displayName || user?.email || 'U')[0].toUpperCase()}
                        </div>
                      )}
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">
                          {user?.displayName || 'User'}
                        </p>
                        <p className="text-xs text-gray-500 truncate">
                          {user?.email}
                        </p>
                      </div>
                    </div>
                    {showHostApplication && (
                      <button
                        onClick={() => {
                          setIsMenuOpen(false);
                          setShowHostApplicationModal(true);
                        }}
                        className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-base font-medium text-emerald-700 hover:bg-emerald-50 transition-colors"
                      >
                        <User className="w-5 h-5" />
                        Apply to Host
                      </button>
                    )}
                    <button
                      onClick={() => {
                        setIsMenuOpen(false);
                        handleLogout();
                      }}
                      className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-base font-medium text-gray-700 hover:text-red-700 hover:bg-red-50/50 transition-colors mt-2"
                    >
                      <LogOut className="w-5 h-5" />
                      Sign Out
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <button
                    onClick={() => {
                      setIsMenuOpen(false);
                      navigate('/login');
                    }}
                    className="block w-full mt-2 text-gray-700 hover:text-emerald-700 text-base px-6 py-3 rounded-lg transition duration-300 font-medium text-center border border-gray-200 hover:border-emerald-300"
                  >
                    Sign In
                  </button>
                  <button
                    onClick={() => {
                      setIsMenuOpen(false);
                      navigate('/signup');
                    }}
                    className="block w-full mt-2 bg-emerald-700 hover:bg-emerald-800 text-white text-base px-6 py-3 rounded-lg transition duration-300 shadow-lg font-medium text-center"
                  >
                    Become a Host
                  </button>
                </>
              )}
            </nav>
          </div>
        )}
      </div>

      {/* Host Application Modal */}
      {user && (
        <HostApplicationModal
          isOpen={showHostApplicationModal}
          onClose={() => setShowHostApplicationModal(false)}
          userId={user.uid}
        />
      )}
    </header>
  );
};

export default Header;
